/**
 * Lenkesiden og lenkepersonen (sak 232, T6; migrering 0463).
 *
 * ---------------------------------------------------------------------------------------------
 * EN LENKEPERSON HAR INGEN INNLOGGING. Hen får én personlig lenke per organisasjon og årgang
 * (`recipient_links`, 232.1), og siden bak den viser bare det som er delt med personen: ressurs,
 * vakt og programpost, med faste felt. Ingen samtale, ingen andre mottakere, aldri økonomi eller
 * kontrakt. Kontaktinformasjon står bare for festivalens egne folk som er satt som kontakt.
 *
 * Ukjent, trukket, utløpt og gammel lenke (etter «Lag ny lenke») er det samme svaret: 404. Et
 * svar som skilte dem ville bekreftet at lenken en gang fantes.
 */
import { z } from 'zod';

import { festivalKindSchema } from '../enums.js';
import { sharedFileSchema, sharedFolderSchema, sharedMapSchema } from './share-content.js';
import { personalPageSchema } from './personal-page.js';
import { sharedVenueViewSchema } from './shared-venue-view.js';

/** Det en lenkedeling kan gjelde (PLAN 1.2, `ck_object_grants__link_types`). F14: kjøreplanen. */
export const LINK_PAGE_OBJECT_TYPES = [
  'resource',
  'shift',
  'performance',
  'stage',
  /* 244.11 (0804): salen og arrangementet fra spillestedet. */
  'venue_room',
  'venue_night',
] as const;
export type LinkPageObjectType = (typeof LINK_PAGE_OBJECT_TYPES)[number];
export const linkPageObjectTypeSchema = z
  .enum(LINK_PAGE_OBJECT_TYPES)
  .meta({ id: 'LinkPageObjectType' });

/** Festivalens kontaktperson for mottakeren — et medlem av organisasjonen, aldri en annen mottaker. */
export const linkPageContactSchema = z
  .object({
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
  })
  .meta({ id: 'LinkPageContact' });
export type LinkPageContact = z.infer<typeof linkPageContactSchema>;

/** Åpningstiden på en ressurs, som klokkeslett i årgangens sone (`HH:MM`). */
export const linkPageOpeningHoursSchema = z
  .object({ opensAt: z.string(), closesAt: z.string() })
  .meta({ id: 'LinkPageOpeningHours' });

/**
 * MER PÅ LENKESIDEN (F11) OG KJØREPLANEN (F14) — bare lesing, og aldri økonomi eller kontrakt.
 *
 * Én form for alle typene, med tomme lister der typen ikke har noe: siden tegner det som finnes.
 * Leverandøren og den innleide sjåføren skal kunne møte opp med bare lenken.
 */
/** Get-in, soundcheck og de andre punktene i timeplanen. `cue` er et spillesteds faste tider. */
export const LINK_PAGE_CUES = ['get_in', 'doors', 'curfew'] as const;
export const linkPageTimeSchema = z
  .object({
    /** Organisasjonens eget navn på punktet. Null når `cue` sier hva det er. */
    title: z.string().nullable(),
    cue: z.enum(LINK_PAGE_CUES).nullable(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
    location: z.string().nullable(),
  })
  .meta({ id: 'LinkPageTime' });
export type LinkPageTime = z.infer<typeof linkPageTimeSchema>;

/** Hvor det er: scenen, ressursen eller salen, med adresse og/eller punktet på kartet. */
export const linkPageLocationSchema = z
  .object({
    name: z.string().nullable(),
    address: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
  })
  .meta({ id: 'LinkPageLocation' });
export type LinkPageLocation = z.infer<typeof linkPageLocationSchema>;

/** Et vedlegg i advancen. Lastes ned gjennom lenken, aldri med en adresse til lageret. */
export const linkPageAttachmentSchema = z
  .object({
    id: z.uuid(),
    filename: z.string(),
    byteSize: z.number().int().nonnegative(),
  })
  .meta({ id: 'LinkPageAttachment' });
export type LinkPageAttachment = z.infer<typeof linkPageAttachmentSchema>;

/** Én reservasjon av en delt ressurs, og hvem som har den. Bare navn, aldri kontaktinformasjon. */
export const linkPageReservationSchema = z
  .object({
    title: z.string(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    quantity: z.number().int().nullable(),
    /** Avdelingen eller personen reservasjonen står på. */
    holder: z.string().nullable(),
    /** Folkene på reservasjonen («hvem som kjører»). */
    people: z.array(z.string()),
    note: z.string().nullable(),
  })
  .meta({ id: 'LinkPageReservation' });
export type LinkPageReservation = z.infer<typeof linkPageReservationSchema>;

/** Én linje i kjøreplanen: settet (`cue` null) eller et punkt i timeplanen til programposten. */
export const linkPageRunningOrderEntrySchema = z
  .object({
    /** Festivaldagen (årgangens «Døgnet begynner»), `YYYY-MM-DD`. */
    day: z.iso.date(),
    performance: z.string().nullable(),
    cue: z.string().nullable(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'LinkPageRunningOrderEntry' });
export type LinkPageRunningOrderEntry = z.infer<typeof linkPageRunningOrderEntrySchema>;

/** §5.3 (1104): én vakt på en delt scene, med navnene på dem som står på. Aldri kontaktinformasjon. */
export const linkPageShiftSchema = z
  .object({
    title: z.string(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    needed: z.number().int(),
    people: z.array(z.string()),
  })
  .meta({ id: 'LinkPageShift' });
export type LinkPageShift = z.infer<typeof linkPageShiftSchema>;

/** §5.3 (1104): den tekniske advancen på én programpost på en delt scene. */
export const linkPageTechnicalSchema = z
  .object({
    performance: z.string().nullable(),
    startsAt: z.iso.datetime().nullable(),
    items: z.array(
      z.object({
        category: z.string().nullable(),
        name: z.string(),
        specification: z.string().nullable(),
        status: z.string(),
      }),
    ),
  })
  .meta({ id: 'LinkPageTechnical' });
export type LinkPageTechnical = z.infer<typeof linkPageTechnicalSchema>;

export const linkPageMoreSchema = z
  .object({
    times: z.array(linkPageTimeSchema),
    location: linkPageLocationSchema.nullable(),
    attachments: z.array(linkPageAttachmentSchema),
    reservations: z.array(linkPageReservationSchema),
    runningOrder: z.array(linkPageRunningOrderEntrySchema),
    /** Kjøreplanens dager. Tom er alle dagene. */
    days: z.array(z.iso.date()),
    /** §5.3 (1104): vaktene på en delt scene. Tom for alle andre typer. */
    shifts: z.array(linkPageShiftSchema).default([]),
    /** §5.3 (1104): den tekniske advancen på programpostene på en delt scene. */
    technical: z.array(linkPageTechnicalSchema).default([]),
  })
  .meta({ id: 'LinkPageMore' });
export type LinkPageMore = z.infer<typeof linkPageMoreSchema>;

export const linkPageItemSchema = z
  .object({
    grantId: z.uuid(),
    objectType: linkPageObjectTypeSchema,
    /** Null for en programpost uten tittel og uten artist. Siden skriver typens navn da. */
    title: z.string().nullable(),
    startsAt: z.iso.datetime().nullable(),
    endsAt: z.iso.datetime().nullable(),
    /** Scenen for en programpost, ressursen eller avdelingen for en vakt. */
    place: z.string().nullable(),
    /** Rollen på en vakt, undertittelen på en ressurs. */
    details: z.string().nullable(),
    openingHours: linkPageOpeningHoursSchema.nullable(),
    confirmedAt: z.iso.datetime().nullable(),
    contact: linkPageContactSchema.nullable(),
    /** F11/F14: tider, sted, vedlegg, reservasjoner og kjøreplan. */
    more: linkPageMoreSchema.nullable().default(null),
    /** F1: meldingen fra den som delte («Rigg fra 08, ring meg ved porten»). */
    note: z.string().nullable().default(null),
    /**
     * 244.11: salens spesifikasjon eller arrangementet, det samme lesedokumentet en ekstern får
     * (`app.link_venue_views`). Null for alle andre typer.
     */
    venue: sharedVenueViewSchema.nullable().default(null),
  })
  .meta({ id: 'LinkPageItem' });
export type LinkPageItem = z.infer<typeof linkPageItemSchema>;

/** F12: et delt dokument eller en delt mappe på lenkesiden (0661, `app.link_content`). */
export const linkPageDocumentSchema = z
  .object({
    grantId: z.uuid(),
    objectType: z.enum(['document', 'folder']),
    title: z.string(),
    confirmedAt: z.iso.datetime().nullable(),
    contact: linkPageContactSchema.nullable(),
    folders: z.array(sharedFolderSchema),
    files: z.array(sharedFileSchema),
  })
  .meta({ id: 'LinkPageDocument' });
export type LinkPageDocument = z.infer<typeof linkPageDocumentSchema>;

/** F13: et delt kart og område på lenkesiden. */
export const linkPageMapSchema = z
  .object({
    grantId: z.uuid(),
    title: z.string(),
    confirmedAt: z.iso.datetime().nullable(),
    contact: linkPageContactSchema.nullable(),
    map: sharedMapSchema,
  })
  .meta({ id: 'LinkPageMap' });
export type LinkPageMap = z.infer<typeof linkPageMapSchema>;

/** `GET /v1/share-links/:token` — hele siden, uten innlogging. */
export const linkPageSchema = z
  .object({
    organizationName: z.string(),
    festivalName: z.string(),
    /** QA6-K4 M5 (0486): på et spillested er årgangen skjult, og siden viser den ikke. */
    festivalKind: festivalKindSchema.optional(),
    editionName: z.string(),
    /** Årgangens sone; tidene over er UTC og vises i denne. */
    timeZone: z.string(),
    recipientName: z.string(),
    items: z.array(linkPageItemSchema),
    /* F12 og F13: dokumentene, mappene og kartene som er delt med lenken. */
    documents: z.array(linkPageDocumentSchema).optional(),
    maps: z.array(linkPageMapSchema).optional(),
    /* 1090: den personlige siden (vakter, akkreditering, kontakt, Mine opplysninger). Null for en ren lenkeperson. */
    personal: personalPageSchema.nullable().optional(),
  })
  .meta({ id: 'LinkPage' });
export type LinkPage = z.infer<typeof linkPageSchema>;

/** `POST /v1/share-links/:token/confirm` — «Bekreft mottatt» på én deling. */
export const confirmLinkItemRequestSchema = z
  .object({ grantId: z.uuid() })
  .strict()
  .meta({ id: 'ConfirmLinkItemRequest' });
export type ConfirmLinkItemRequest = z.infer<typeof confirmLinkItemRequestSchema>;

export const confirmLinkItemResponseSchema = z
  .object({ grantId: z.uuid(), confirmedAt: z.iso.datetime() })
  .meta({ id: 'ConfirmLinkItemResponse' });
export type ConfirmLinkItemResponse = z.infer<typeof confirmLinkItemResponseSchema>;

/**
 * LENKEPERSONENE PÅ ÅRGANGEN, slik organisasjonen ser dem i personlista (232, «Organisasjonen ser om
 * lenken er åpnet og om delingen er bekreftet»). Én rad per person med en levende lenke.
 */
export const linkPersonStatusSchema = z
  .object({
    personId: z.uuid(),
    /** Når den levende lenken sist ble laget («Lag ny lenke» lager en ny). */
    linkCreatedAt: z.iso.datetime(),
    firstOpenedAt: z.iso.datetime().nullable(),
    lastOpenedAt: z.iso.datetime().nullable(),
    /** Levende lenkedelinger til personen i årgangen. */
    sharedCount: z.number().int().nonnegative(),
    confirmedCount: z.number().int().nonnegative(),
  })
  .meta({ id: 'LinkPersonStatus' });
export type LinkPersonStatus = z.infer<typeof linkPersonStatusSchema>;

export const linkPersonListResponseSchema = z
  .object({ items: z.array(linkPersonStatusSchema) })
  .meta({ id: 'LinkPersonListResponse' });
export type LinkPersonListResponse = z.infer<typeof linkPersonListResponseSchema>;
